import React, { useRef, useState, useEffect } from "react";
import styles from "styles.module.css";
//@ts-ignore
import "./style.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addYears, addMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { storageStatisticsGridColumnItems } from "./GridColumns";
import { getStorageStatisticsApi, StorageStatisticFilter } from "api/sales";
import ColumnDropdown from "./utils/dropdown";
import Filter from "./Filter";
import { SalesStatistics } from "interface/sales";
import { useInfoStore } from "store/error";
import { CustomError } from "interface";
import { GridColumnProps } from "@progress/kendo-react-grid";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { aggregatesFields } from "./utils/data";
import { GroupHeaderCellGrossWeight } from "ui/grid/CustomGrid";
import Loading from "ui/Loading";
import StoreStatisticsMobileList from "./StoreStatisticsMobileList";
import { useUserStore } from "store/user";

const StoreStatisticsPage = () => {
	const { t } = useTranslation();

	const gridRef = useRef<HTMLDivElement>(null);
	// const checkItemsRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [allColumns, setAllColumns] = useState<GridColumnProps[]>([]);
	const columnsRef = useRef<GridColumnProps[]>([]);
	const [showPopup, setShowPopup] = useState<boolean>(false);
	const [refReady, setRefReady] = useState<boolean>(false);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const headerRef = useRef<HTMLImageElement | null>(null);
	const [data, setData] = useState<SalesStatistics[]>([]);
	const filteredDataRef = useRef<SalesStatistics[]>([]);
	const [isLoading, setLoading] = useState<boolean>(false);

	const { setError } = useInfoStore();
	const { isAdmin } = useUserStore();

	const [filterOptions, setFilterOptions] = useState<StorageStatisticFilter>({
		fromDate: isAdmin ? addMonths(new Date(), -2) : addYears(new Date(), -1),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	useEffect(() => {
		const updateMobileView = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		updateMobileView();
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (headerRef.current) {
			setRefReady(true);
		}
	}, [headerRef.current]);

	useEffect(() => {
		const newColumns = storageStatisticsGridColumnItems({
			ref: headerRef,
			data: data,
			filteredDataRef: filteredDataRef,
		});
		columnsRef.current = newColumns;
		setAllColumns(newColumns);
	}, [refReady, data]);

	useEffect(() => {
		columnsRef.current = allColumns;
	}, [allColumns]);

	useEffect(() => {
		setLoading(true);
		getStorageStatisticsApi(filterOptions)
			.then((response) => {
				setData(response.data);
				filteredDataRef.current = response.data;
				setForceUpdate((prev) => !prev);
			})
			.catch((error) => {
				if (error.response?.data) {
					setError(new CustomError("Something went wrong", "error"));
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [filterOptions, setError]);

	useEffect(() => {
		const updateColumnWidths = () => {
			const columns = columnsRef.current;
			if (columns.length === 0) return;
			const grid = document.querySelector(".k-grid") as HTMLElement;
			const table = document.querySelector(".k-table") as HTMLElement;
			if (!grid || !table) return;

			const tableWidth = table.offsetWidth;
			const gridWidth = grid.offsetWidth;
			if (!gridWidth || !tableWidth) return;

			const addingWidth = gridWidth - tableWidth;
			const productWidthDefault = 250;
			let incrementedWidth = 0;
			const incrementValue = 10;

			if (addingWidth < -10) {
				let updatedNeeded = false;
				const updatedColumns = columns.map((column) => {
					if (
						column.field === "productDescription" &&
						parseWidth(column.width) > productWidthDefault
					) {
						updatedNeeded = true;
						const width = parseWidth(column.width) - Math.abs(addingWidth);
						column.width = Math.max(width, productWidthDefault);
					}
					return column;
				});

				if (updatedNeeded) {
					columnsRef.current = updatedColumns;
					setAllColumns(updatedColumns);
				}
				return;
			}
			if (addingWidth <= 0) return;

			let isProductDescriptionColumnHidden = columns.find(
				(col) => col.field === "productDescription"
			)?.hidden;

			const updatedColumns = columns.map((column) => {
				let newColumn = { ...column };

				if (isProductDescriptionColumnHidden) {
					if (!newColumn.hidden && incrementedWidth < addingWidth) {
						incrementedWidth += incrementValue;
						newColumn.width = parseWidth(newColumn.width) + incrementValue;
					}
				} else {
					if (newColumn.field === "productDescription") {
						let width = parseWidth(newColumn.width);
						if (width > productWidthDefault) {
							width = productWidthDefault;
						}
						newColumn.width = width + addingWidth;
					}
				}
				return newColumn;
			});

			if (updatedColumns.length === 0) {
				console.error("No columns to update widths for.");
				return;
			}

			columnsRef.current = updatedColumns;
			setAllColumns(updatedColumns);
		};

		if (isMobileView) {
			return;
		}

		const timeoutId = setTimeout(() => {
			updateColumnWidths();
		}, 70);

		const kendoGrid = document.querySelector(".k-grid") as HTMLElement;
		if (!kendoGrid) {
			return () => clearTimeout(timeoutId);
		}

		const handleResize = () => {
			updateColumnWidths();
		};

		updateColumnWidths();

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
			clearTimeout(timeoutId);
		};
	}, [data, forceUpdate, isMobileView]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const handleCheckboxChange = (field: string, checked: boolean) => {
		setAllColumns((prevItems) =>
			prevItems.map((item) =>
				item.field === field ? { ...item, hidden: checked } : item
			)
		);
		setForceUpdate((prev) => !prev);
	};

	const closeColumnDropdown = () => setShowPopup(false);

	if (!isMobileView && isLoading) {
		return (
			<div className={styles.PageContainer}>
				<Loading show={isLoading} />
			</div>
		);
	}
	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("StoreStatistics")}
				</legend>
			</div>

			<div ref={checkItemsRef}>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<StoreStatisticsMobileList items={data} loading={isLoading} />
				) : (
					<GridWithGroupPaging
						gridData={data}
						gridColumns={allColumns}
						itemAboveHeight={70}
						dataKey="contractNoteNumber"
						className="sales-statistics-grid"
						exportGridAsExcelRef={excelRef}
						groupHeaderCell={GroupHeaderCellGrossWeight}
						groupable={{ footer: "visible", enabled: true }}
						aggregateFields={aggregatesFields}
						pageable={false}
						loading={isLoading}
						excelProps={{
							fileName: `store_statistics_${new Date().toISOString()}.xlsx`,
						}}
						groupLargeColumn={[{ field: "grossWeight", width: 270 }]}
						onFilteredDataChange={(filtered) => {
							filteredDataRef.current = filtered;
						}}
					/>
				)}
			</div>

			{!isMobileView && refReady && headerRef.current && (
				<ColumnDropdown
					show={showPopup}
					onClose={closeColumnDropdown}
					items={allColumns}
					onCheckboxChange={handleCheckboxChange}
					anchor={headerRef}
					forceUpdate={forceUpdate}
				/>
			)}
		</div>
	);
};

export default StoreStatisticsPage;

const parseWidth = (width: string | number | undefined): number => {
	if (width === undefined) return 0;

	if (typeof width === "number") {
		return width;
	}

	if (typeof width === "string") {
		// Remove 'px' and convert to number
		return parseInt(width.replace("px", ""), 10) || 0;
	}

	return 0;
};
